import { z } from 'zod';

import {
  EllucianAdmissionApplicationRecord,
  EllucianDataMedicalDetails,
  EllucianGradesRecord,
  EllucianMatchingStatus,
} from '../../integrations/ellucian/index.js';
import { DeclarationsFieldVisibility } from '../api-contracts/index.js';
import {
  AddressSchema,
  ApplicantDetails,
  ApplicantEmploymentSchema,
  AttachDocumentSchema,
  EnglishSchema,
  IStudyHistory,
  PhoneNumberSchema,
} from '../applicant/index.js';
import { ApplicationRefereeRelation, ApplicationStatus, FeeStatus, FundingOption } from '../enums.js';
import { ApplicationTaskRoute, RouteStatus, SectionRoute, YourInformationTaskRoute } from '../route/index.js';
import { IUserRole } from '../../user/index.js';

export const FundingPlanSchema = z.object({
  primaryFundingSource: z.nativeEnum(FundingOption),
  otherSourceOfFunding: z.string().nullish(),
  isSLCGrant: z.boolean().nullish(),
  isSLCLoan: z.boolean().nullish(),
  isPlanningToWork: z.boolean(),
});

export interface FundingFieldVisibility {
  disableTuitionFeeQuestion?: boolean;
  disableMaintenanceQuestion?: boolean;
  disableWorkQuestion?: boolean;
}

type FundingPlanSchemaInput = z.input<typeof FundingPlanSchema>;
type FundingPlanSchemaOutput = z.output<typeof FundingPlanSchema>;

export function enhanceFundingPlanSchema(
  feeStatus: FeeStatus,
  fieldVisibility: FundingFieldVisibility = {},
): z.ZodEffects<z.ZodTypeAny, FundingPlanSchemaOutput, FundingPlanSchemaInput> {
  const {
    disableTuitionFeeQuestion = false,
    disableMaintenanceQuestion = false,
    disableWorkQuestion = false,
  } = fieldVisibility;

  // Start with the default schema
  let fundingSchema: z.ZodTypeAny = FundingPlanSchema;

  // For Home students with no disabled fields, enhance the schema to require SLC fields
  if (
    feeStatus === FeeStatus.Home &&
    !disableTuitionFeeQuestion &&
    !disableMaintenanceQuestion &&
    !disableWorkQuestion
  ) {
    fundingSchema = z.object({
      primaryFundingSource: z.nativeEnum(FundingOption),
      otherSourceOfFunding: z.string().nullish(),
      isSLCGrant: z.boolean(),
      isSLCLoan: z.boolean(),
      isPlanningToWork: z.boolean(),
    });
  } else if (
    fieldVisibility.disableTuitionFeeQuestion ||
    fieldVisibility.disableMaintenanceQuestion ||
    fieldVisibility.disableWorkQuestion
  ) {
    // If any fields are disabled, build a custom schema
    const schemaShape: Record<string, z.ZodTypeAny> = {
      primaryFundingSource: z.nativeEnum(FundingOption),
      otherSourceOfFunding: z.string().nullish(),
    };

    // Handle SLC fields based on fee status and visibility
    if (feeStatus === FeeStatus.Home) {
      schemaShape.isSLCGrant = disableTuitionFeeQuestion ? z.boolean().nullish() : z.boolean();
      schemaShape.isSLCLoan = disableMaintenanceQuestion ? z.boolean().nullish() : z.boolean();
    } else {
      schemaShape.isSLCGrant = z.boolean().nullish();
      schemaShape.isSLCLoan = z.boolean().nullish();
    }

    // Handle work field
    schemaShape.isPlanningToWork = disableWorkQuestion ? z.boolean().nullish() : z.boolean();

    fundingSchema = z.object(schemaShape);
  }

  // Apply refinements
  return fundingSchema.superRefine((data, ctx) => {
    const { primaryFundingSource } = data;

    if (primaryFundingSource === FundingOption.Other) {
      if (!data.otherSourceOfFunding?.trim?.()?.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['otherSourceOfFunding'],
          message: 'Description of other source of funding is required',
        });
      }
    }
    return true;
  });
}

export const PersonalStatementSchema = z.object({
  hasPersonalStatementDocument: z.boolean(),
  personalStatementDocument: AttachDocumentSchema.optional(),
});

export const PersonalStatementSchemaWithDependencies = (
  fileNames?: string[],
): z.ZodEffects<typeof PersonalStatementSchema> => {
  return PersonalStatementSchema.superRefine((data, ctx) => {
    if (data.hasPersonalStatementDocument) {
      const hasFiles = fileNames && fileNames.length > 0;

      if (!hasFiles) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Personal statement file is required.',
          path: ['fileUpload'],
        });
      }
    }
  });
};

export const ResearchProposalSchema = z.object({
  hasResearchProposalDocument: z.boolean(),
  researchProposalDocument: AttachDocumentSchema.optional(),
});

export const ResearchProposalSchemaWithDependencies = (
  fileNames?: string[],
): z.ZodEffects<typeof ResearchProposalSchema> => {
  return ResearchProposalSchema.superRefine((data, ctx) => {
    if (data.hasResearchProposalDocument) {
      const hasFiles = fileNames && fileNames.length > 0;

      if (!hasFiles) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Research proposal file is required.',
          path: ['fileUpload'],
        });
      }
    }
  });
};

// Reusable helper for file validation with dependencies
export const createFileValidationSchema = <T extends z.ZodTypeAny>(
  baseSchema: T,
  fileRequiredField: string,
  errorMessage: string,
  errorPath: string[] = ['fileUpload'],
) => {
  return (fileNames?: string[]): z.ZodEffects<T> => {
    return baseSchema.superRefine((data: any, ctx) => {
      if (data[fileRequiredField]) {
        const hasFiles = fileNames && fileNames.length > 0;

        if (!hasFiles) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMessage,
            path: errorPath,
          });
        }
      }
    });
  };
};

export const RefereeSchema = AddressSchema.extend({
  name: z.string().min(1, "Name can't be empty"),
  email: z.string().email(),
  phone: PhoneNumberSchema,
  relationship: z.nativeEnum(ApplicationRefereeRelation, { message: 'Please provide the value' }),
  otherRelationshipDetails: z.string().optional(),
  organisation: z.string().min(1, "Organisation can't be empty"),
  occupation: z.string().min(1, "Occupation can't be empty"),
  yearsKnown: z.number(),
}).superRefine((data, ctx) => {
  if (data.relationship === ApplicationRefereeRelation.Other && !data.otherRelationshipDetails) {
    ctx.addIssue({
      code: 'custom',
      path: ['otherRelationshipDetails'],
      message: 'Other Relationship details are required',
    });
  }

  const stringHasContent = (str: string | undefined): boolean => {
    return str !== undefined && str.trim().length > 0;
  };

  if (data.addressHasSubdivision && (!stringHasContent(data.subdivisionCode) || !stringHasContent(data.state))) {
    ctx.addIssue({
      code: 'custom',
      path: ['state'],
      message: 'State/Province is required when the address has a subdivision',
    });
  }

  if (data.country === 'United Kingdom') {
    if (!stringHasContent(data.state)) {
      ctx.addIssue({
        code: 'custom',
        path: ['state'],
        message: 'County is required for United Kingdom addresses',
      });
    }
  }
});

export const ReferencesSchema = z.object({
  hasReferee: z.boolean(),
  references: z.array(RefereeSchema).nullish(),
  referenceLetterDocument: AttachDocumentSchema.optional(),
});

// References schema with file validation - updated to make reference letter optional
export const ReferencesSchemaWithFiles = (_fileNames?: string[]): z.ZodEffects<typeof ReferencesSchema> => {
  return ReferencesSchema.superRefine((_data, _ctx) => {
    // Files are optional; do not enforce file presence even when hasReferee is true.
  });
};

// Employment schema with file validation for CV upload
export const ApplicantEmploymentSchemaWithFiles = (
  fileNames?: string[],
): z.ZodEffects<typeof ApplicantEmploymentSchema> => {
  return ApplicantEmploymentSchema.superRefine((data, ctx) => {
    // Only validate files if hasCV is true
    if (data.hasCV) {
      const hasFiles = fileNames && fileNames.length > 0;
      // file is optional as long as we enter employment history manually.
      const hasEmploymentHistory = data.employmentHistory && data.employmentHistory.length > 0;

      const hasEnteredData = hasFiles || hasEmploymentHistory;

      if (!hasEnteredData) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CV file is required when CV option is selected.',
          path: ['fileUpload'],
        });
      }
    }
  });
};

// English schema with file validation for English test document upload - updated to make files optional
export const EnglishSchemaWithFiles = (_fileNames?: string[]): z.ZodEffects<typeof EnglishSchema> => {
  return EnglishSchema.superRefine((_data, _ctx) => {
    // Files are optional; do not enforce file presence even when hasEnglishTest is true.
  });
};

export const AgentSchema = z.object({
  name: z.string().nullish(),
  agency: z.string().nullish(),
  email: z.string().email().nullish(),
});

export const OtherPresentative = z.object({
  relation: z.string().nullish(),
  name: z.string().nullish(),
});

export const RepresentationSchema = z
  .object({
    hasRepresentative: z.boolean({
      required_error: 'Please select whether anyone is helping you submit your application',
    }),
    isHelpedByAgent: z.boolean().optional(),
    isHelpedByFamilyMember: z.boolean().optional(),
    isHelpedByFriend: z.boolean().optional(),
    isHelpedBySchoolCounsellor: z.boolean().optional(),
    isHelpedByTeacher: z.boolean().optional(),
    isHelpedByStudent: z.boolean().optional(),
    isHelpedByOther: z.boolean().optional(),
    studentName: z.string().nullish(),
    teacherName: z.string().nullish(),
    schoolCounsellorName: z.string().nullish(),
    friendName: z.string().nullish(),
    familyMemberName: z.string().nullish(),
    consent: z.boolean().nullish(),
    agent: AgentSchema.nullish(),
    otherPresentative: OtherPresentative.nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.hasRepresentative === true) {
      const helpOptions = [
        data.isHelpedByAgent,
        data.isHelpedByFamilyMember,
        data.isHelpedByFriend,
        data.isHelpedBySchoolCounsellor,
        data.isHelpedByTeacher,
        data.isHelpedByStudent,
        data.isHelpedByOther,
      ];
      if (!helpOptions.some(option => option === true)) {
        ctx.addIssue({
          code: 'custom',
          path: ['representationType'],
          message: 'At least one representation type must be selected',
        });
      }

      if (helpOptions.some(option => option === true) && data?.consent === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['consent'],
          message: 'Consent is required',
        });
      }
    }

    const validationRules = [
      {
        condition: data.isHelpedByAgent && !data.agent?.name,
        path: 'agent.name',
        message: 'Agent name is required',
      },
      {
        condition: data.isHelpedByAgent && !data.agent?.email,
        path: 'agent.email',
        message: 'Agent email is required',
      },
      {
        condition: data.isHelpedByAgent && !data.agent?.agency,
        path: 'agent.agency',
        message: 'Agent agency is required',
      },
      {
        condition: data.isHelpedByFamilyMember && !data.familyMemberName,
        path: 'familyMemberName',
        message: 'Family member name is required',
      },
      {
        condition: data.isHelpedByFriend && !data.friendName,
        path: 'friendName',
        message: 'Friend name is required',
      },
      {
        condition: data.isHelpedBySchoolCounsellor && !data.schoolCounsellorName,
        path: 'schoolCounsellorName',
        message: 'School counsellor name is required',
      },
      {
        condition: data.isHelpedByTeacher && !data.teacherName,
        path: 'teacherName',
        message: 'Teacher name is required',
      },
      {
        condition: data.isHelpedByStudent && !data.studentName,
        path: 'studentName',
        message: 'Student name is required',
      },
      {
        condition: data.isHelpedByOther && !data.otherPresentative?.name,
        path: 'otherPresentative.name',
        message: 'Other person name is required',
      },
      {
        condition: data.isHelpedByOther && !data.otherPresentative?.relation,
        path: 'otherPresentative.relation',
        message: 'Other person relation is required',
      },
    ];

    // Apply all validation rules
    for (const rule of validationRules) {
      if (rule.condition) {
        ctx.addIssue({
          code: 'custom',
          path: [rule.path],
          message: rule.message,
        });
      }
    }
  });

export const RepresentationFormSchema = z.object({
  representative: RepresentationSchema.optional(),
});

export const DeclarationsSchema = z
  .object({
    interruptedStudy: z.boolean(),
    criminalConvictions: z.boolean().nullish(),
    detailedInterruptionReason: z.string().nullish(),
    offenceDetails: z.string().nullish(),
  })
  .superRefine((data, ctx) => {
    // Validate that detailedInterruptionReason is provided when interruptedStudy is true
    if (data.interruptedStudy === true) {
      if (!data.detailedInterruptionReason?.trim?.()?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Detailed interruption reason is required when study was interrupted',
          path: ['detailedInterruptionReason'],
        });
      }
    }

    // Validate that offenceDetails is provided when criminalConvictions is true
    if (data?.criminalConvictions === true) {
      if (!data.offenceDetails?.trim?.()?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Offence details are required when criminal convictions exist',
          path: ['offenceDetails'],
        });
      }
    }
    return true;
  });

// Dynamic schema based on disable flags
export function enhanceDeclarationsSchema(
  fieldVisibility: Partial<DeclarationsFieldVisibility> = {},
): z.ZodEffects<z.ZodObject<Record<string, z.ZodTypeAny>>, Partial<IDeclarations>, Partial<IDeclarations>> {
  const { disableInterruptionQuestion = false, disableConvictionQuestion = false } = fieldVisibility;

  // Build the schema shape dynamically based on field visibility
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  if (!disableInterruptionQuestion) {
    schemaShape.interruptedStudy = z.boolean();
    schemaShape.detailedInterruptionReason = z.string().nullish();
  }

  if (!disableConvictionQuestion) {
    schemaShape.criminalConvictions = z.boolean().nullish();
    schemaShape.offenceDetails = z.string().nullish();
  }

  const declarationsSchema = z.object(schemaShape);

  // Apply refinements for conditional fields
  return declarationsSchema.superRefine((data, ctx) => {
    // Validate detailedInterruptionReason when interruptedStudy is true
    if (!disableInterruptionQuestion && data.interruptedStudy === true) {
      if (!data.detailedInterruptionReason?.trim?.()?.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['detailedInterruptionReason'],
          message: 'Detailed interruption reason is required when study was interrupted',
        });
      }
    }

    // Validate offenceDetails when criminalConvictions is true
    if (!disableConvictionQuestion && data.criminalConvictions === true) {
      if (!data.offenceDetails?.trim?.()?.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['offenceDetails'],
          message: 'Offence details are required when criminal convictions exist',
        });
      }
    }
    return true;
  });
}

export const InformationProcessingConsentSchema = z.object({
  informationProcessingConsent: z.boolean().refine(value => value === true, {
    message: 'You must agree to the information processing consent.',
  }),
});

export const PrivacyPolicyConsentSchema = z.object({
  privacyPolicyConsent: z.boolean().refine(value => value === true, {
    message: 'You must agree to the privacy policy.',
  }),
});

export const TermsAndConditionsConsentSchema = z.object({
  termsAndConditionsConsent: z.boolean().refine(value => value === true, {
    message: 'You must agree to the terms and conditions.',
  }),
});

export const AccuracyOfDataConsentSchema = z.object({
  accuracyOfDataConsent: z.boolean().refine(value => value === true, {
    message: 'You must confirm the accuracy of the provided data.',
  }),
});

export const ConsentSchema = z.object({
  ...InformationProcessingConsentSchema.shape,
  ...PrivacyPolicyConsentSchema.shape,
  ...TermsAndConditionsConsentSchema.shape,
  ...AccuracyOfDataConsentSchema.shape,
});

export type ApplicationContextType = {
  intakeId: string;
  intendedLevelOfStudy: string;
  isApplicationLocked: boolean;
  isApplicationSubmitted: boolean;
  universityName: string;
  permanentAddressLookupEnabled?: boolean;
  contactAddressLookupEnabled?: boolean;
  termAddressLookupEnabled?: boolean;
  ukAddressLookupEnabled?: boolean;
  employmentAddressLookupEnabled?: boolean;
  refereeAddressLookupEnabled?: boolean;
  educationAddressLookupEnabled?: boolean;
};

export interface Application
  extends
    IFundingPlan,
    IPersonalStatement,
    IResearchProposal,
    IReferences,
    IRepresentationForm,
    IConsent,
    IDeclarations,
    Omit<ApplicantDetails, 'routeStatus' | 'sitsData'> {
  userId: string;
  applicationId: string;
  applicationStatus: ApplicationStatus;
  universityName: string;
  universityShortcode?: string; // QOL change, should have been done ages ago
  routeStatus: Partial<Record<ApplicationTaskRoute, RouteStatus>> &
    Partial<Record<YourInformationTaskRoute.ProgrammeChoices, RouteStatus>> &
    Partial<Record<SectionRoute, RouteStatus>>;
  studyHistory: IStudyHistory;
  hasAgent: string; // probably should be boolean
  agentId?: string;
  agentName?: string;
  submittedAt?: string;
  submittedBy?: string;
  ellucianData?: Partial<IEllucianData>;
  sitsData?: Partial<IApplicationSITSData>;

  initiallySubmittedApplication?: Application;

  studentNo?: string;
  studentNumberGeneratedByApply?: boolean;

  initiatedByRole?: IUserRole;
}

export type NotSubmittableApplication = Omit<Application, keyof ApplicantDetails>;

export const StaffAssignAgentSchema = z.object({
  agentId: z.string(),
});

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IStaffAssignAgentSchema extends z.infer<typeof StaffAssignAgentSchema> {}

/* eslint-disable @typescript-eslint/no-empty-object-type */

export interface IFundingPlan extends z.infer<typeof FundingPlanSchema> {}
export interface IPersonalStatement extends z.infer<typeof PersonalStatementSchema> {}
export interface IResearchProposal extends z.infer<typeof ResearchProposalSchema> {}
export interface IReferences extends z.infer<typeof ReferencesSchema> {}
export interface IReferee extends z.infer<typeof RefereeSchema> {}

export interface IDeclarations extends z.infer<typeof DeclarationsSchema> {}
export interface IConsent extends z.infer<typeof ConsentSchema> {}
export interface IRepresentation extends z.infer<typeof RepresentationSchema> {}
export interface IRepresentationForm extends z.infer<typeof RepresentationFormSchema> {}

export interface IEllucianData {
  personMatchingId: string;
  personId: string;
  admissionApplicationRecord: Array<EllucianAdmissionApplicationRecord>;
  matchingStatus: EllucianMatchingStatus;
  medicalDetails: EllucianDataMedicalDetails;
  gradesRecord: EllucianGradesRecord;
  accommodationPortalUrl: string;
  bannerId: string;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface IApplicationSITSData {
  applicationFormSequence?: string;
  sequence?: string;
}
